// Variant split — owner, 2026-11-02 (web clarity redesign, part 4).
//
// Many supplier listings send every variant as ONE long string on one axis
// («باب منزلق أسود 80 سم تعليق مزدوج، طول 80*عرض 50*ارتفاع 185»), which
// renders as 18+ near-identical chips. This file splits such an axis into its
// real attributes (colour · size · type) with deterministic rules, read from
// the English label (the most regular of the three) and shown in Arabic.
//
// The result never invents a combination: every split value points back to
// the axis value it came from, so a full selection resolves to exactly one
// original value — and through skuFor() to exactly one real supplier SKU,
// which is what the automated purchase uses.
//
// Pure functions, no imports: the same file runs in the browser, in tests and
// in the catalog dry run.

export const PARSER_VERSION = 1;
const FOLD_OVER = 6;            // more values than this that cannot be split → folded
const OUTLIER_SHARE = 0.1;      // up to 10% of values may not fit the pattern (e.g. «Design 1»)
const MIN_COVERAGE = 0.6;       // the attributes must really cross: ≥60% of their combinations exist
// «Design 1» / «Style 3» / «款式2» — a numbered picture, never a size
const NUMBERED_RE = /^\s*(design|style|model|pattern|picture|photo|option|no\.?|款式|款|图)\s*[#:]?\s*\d+\s*$/i;

// English colour phrases → Arabic. Longest first, so «warm white» wins over «white».
const COLORS = [
  ['rose gold', 'ذهبي وردي'], ['warm white', 'أبيض دافئ'], ['off white', 'أبيض مطفي'], ['off-white', 'أبيض مطفي'], ['milky white', 'أبيض حليبي'],
  ['light grey', 'رمادي فاتح'], ['light gray', 'رمادي فاتح'], ['dark grey', 'رمادي غامق'], ['dark gray', 'رمادي غامق'],
  ['light blue', 'أزرق فاتح'], ['dark blue', 'أزرق غامق'], ['sky blue', 'أزرق سماوي'], ['navy blue', 'كحلي'], ['light green', 'أخضر فاتح'], ['dark green', 'أخضر غامق'],
  ['light pink', 'وردي فاتح'], ['wine red', 'نبيتي'], ['dark brown', 'بني غامق'], ['light brown', 'بني فاتح'], ['army green', 'زيتي'], ['olive green', 'زيتي'],
  ['black', 'أسود'], ['white', 'أبيض'], ['grey', 'رمادي'], ['gray', 'رمادي'], ['silver', 'فضي'], ['gold', 'ذهبي'], ['golden', 'ذهبي'],
  ['red', 'أحمر'], ['blue', 'أزرق'], ['navy', 'كحلي'], ['green', 'أخضر'], ['yellow', 'أصفر'], ['orange', 'برتقالي'], ['pink', 'وردي'],
  ['purple', 'بنفسجي'], ['violet', 'بنفسجي'], ['brown', 'بني'], ['beige', 'بيج'], ['khaki', 'كاكي'], ['coffee', 'قهوة'], ['champagne', 'شامبين'],
  ['transparent', 'شفاف'], ['clear', 'شفاف'], ['walnut', 'جوزي'], ['oak', 'بلوطي'], ['ivory', 'عاجي'], ['cream', 'كريمي'], ['apricot', 'مشمشي'],
  ['turquoise', 'فيروزي'], ['cyan', 'سماوي'], ['burgundy', 'عنابي'], ['maroon', 'عنابي'], ['camel', 'جملي'], ['multicolor', 'متعدد الألوان'],
];
const COLOR_AR = new Set(COLORS.map(([, ar]) => ar));
const AR_COLOR_WORDS = ['أسود', 'أبيض', 'رمادي', 'فضي', 'ذهبي', 'أحمر', 'أزرق', 'كحلي', 'أخضر', 'أصفر', 'برتقالي', 'وردي', 'بنفسجي', 'بني', 'بيج', 'كاكي',
  'شفاف', 'دافئ', 'فاتح', 'غامق', 'بيضاء', 'سوداء', 'حمراء', 'زرقاء', 'خضراء', 'صفراء', 'زهري', 'أرجواني', 'لون', 'عاجي', 'كريمي', 'جوزي', 'بلوطي', 'زيتي', 'نبيتي', 'عنابي', 'سماوي', 'فيروزي', 'شامبين', 'مطفي', 'حليبي'];
const UNIT_AR = { mm: 'مم', cm: 'سم', m: 'م', ml: 'مل', l: 'لتر', kg: 'كجم', g: 'جم', w: 'واط', v: 'فولت', mah: 'مللي أمبير', inch: 'إنش', in: 'إنش' };
const APPAREL = ['xxxxxl', 'xxxxl', 'xxxl', 'xxl', 'xl', 'xs', 's', 'm', 'l', '5xl', '4xl', '3xl', '2xl'];

const norm = (s) => String(s || '').toLowerCase().replace(/[，、;；]/g, ',').replace(/\s+/g, ' ').trim();
const reEsc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// L80*W50*H185cm · 80x50x185 cm · 80*50cm — the dimensions, once.
const DIMS_RE = /(?:l(?:ength)?\s*)?(\d+(?:\.\d+)?)\s*(?:cm|mm)?\s*[*x×]\s*(?:w(?:idth)?\s*)?(\d+(?:\.\d+)?)(?:\s*(?:cm|mm)?\s*[*x×]\s*(?:h(?:eight)?\s*)?(\d+(?:\.\d+)?))?\s*(cm|mm|m)?\b/i;
// «1m wide x 1.9m long» / «1.9m long x 1m wide» — dimensions written with words (store test 2026-11-03:
// read as a «1m» size, the rest «wide x 1 9m long» became an English, cut «النوع» on the bed page)
const DIMS_WORDS_RE = /(\d+(?:\.\d+)?)\s*(mm|cm|m)?\s*(wide|width|long|length)\s*[*x×]\s*(\d+(?:\.\d+)?)\s*(mm|cm|m)?\s*(wide|width|long|length)\b/i;
const SIZE_RE = /(?:^|[\s,(])(\d+(?:\.\d+)?)\s*(mm|cm|ml|mah|kg|inch|in|m|l|g|w|v)?(?=$|[\s,)\-+/])/i;

// The British plug (owner, 2026-11-02): Saudi Arabia uses it, so a plug or
// socket standard opens on it and says so. The SAME test as the SQL in
// market_delivered().default_sku (migration 20261102110000) — change both.
//   zh  英规 / 英标 / 英式插            (often machine-translated «English rules»)
//   en  UK / British / Britain + plug / standard / socket / regulation / rules
//   en  «English standard / rules / regulations / plug»
export const UK_PLUG_AR = 'بريطاني (المستخدم في السعودية)';
export function isUkPlug(v) {
  if (!v) return false;
  const en = String(v.label_en || ''); const zh = String(v.label_zh || '');
  return /(英规|英标|英式插)/.test(zh)
    || (/\b(uk|british|britain)\b/i.test(en) && /(plug|standard|socket|regulation|rules)/i.test(en))
    || /\benglish (standard|rules?|regulations?|plug)/i.test(en);
}

/** One label → { color, size, dims, rest } (each null when absent). */
export function parseLabel(labelEn) {
  let s = ` ${norm(labelEn)} `;
  let dims = null;
  const dw = s.match(DIMS_WORDS_RE);
  if (dw) {
    const a = Number(dw[1]), b = Number(dw[4]), firstWide = /^w/i.test(dw[3]);
    dims = { l: firstWide ? b : a, w: firstWide ? a : b, h: null, unit: (dw[5] || dw[2] || 'cm').toLowerCase() };
    s = s.replace(dw[0], ' ');
  }
  const d = dims ? null : s.match(DIMS_RE);
  if (d) {
    dims = { l: Number(d[1]), w: Number(d[2]), h: d[3] ? Number(d[3]) : null, unit: (d[4] || 'cm').toLowerCase() };
    s = s.replace(d[0], ' ');
  }
  let color = null;
  for (const [en, ar] of COLORS) {
    const re = new RegExp(`(^|[^a-z])${reEsc(en)}(?=$|[^a-z])`, 'i');
    if (re.test(s)) { color = { key: en.replace('gray', 'grey').replace('-', ' '), ar }; s = s.replace(re, '$1 '); break; }
  }
  let size = null;
  const app = s.match(new RegExp(`(?:^|[\\s,(])(?:size\\s*)?(${APPAREL.map(reEsc).join('|')})(?=$|[\\s,)])`, 'i'));
  if (app) { size = { key: app[1].toUpperCase(), ar: app[1].toUpperCase() }; s = s.replace(app[0], ' '); }
  if (!size) {
    const m = s.match(SIZE_RE);
    if (m) {
      const unit = (m[2] || (dims ? dims.unit : '')).toLowerCase();
      size = { key: `${m[1]}${unit}`, num: Number(m[1]), unit, ar: `${m[1]}${unit ? ` ${UNIT_AR[unit] || unit}` : ''}` };
      s = s.replace(m[0], ' ');
    }
  }
  const rest = s.replace(/[()[\],+/\-–—:.]+/g, ' ').replace(/\b(style|model|type|size|color|colour|version)\b/g, ' ').replace(/\s+/g, ' ').trim();
  return { color, size, dims, rest: rest || null };
}

/** The Arabic words left once colour, sizes and dimensions are taken out — the «type» to show. */
export function arabicRest(labelAr) {
  let s = String(labelAr || '');
  s = s.replace(/(طول|عرض|ارتفاع|الطول|العرض|الارتفاع)\s*\d+(?:\.\d+)?/g, ' ').replace(/\d+(?:\.\d+)?\s*[*x×]\s*\d+(?:\.\d+)?(?:\s*[*x×]\s*\d+(?:\.\d+)?)?/g, ' ');
  // a unit is a whole word: «1.5 متر» is not «1.5 م» + «تر» (owner, 2026-11-02 — «تر عرض تر طول» on the bed page)
  s = s.replace(/\d+(?:\.\d+)?\s*(ملليمتر|سنتيمتر|متر|أمتار|مم|سم|ملم|م|مل|لتر|كيلوغرام|كجم|كغ|جرام|جم|غ|واط|فولت|إنش|بوصة)?(?![\u0621-\u064A])/g, ' ');
  AR_COLOR_WORDS.forEach((w) => { s = s.replace(new RegExp(`(^|\\s)(?:ال)?${w}(?:ة)?(?=$|[\\s،,])`, 'g'), ' '); });
  s = s.replace(/[،,؛;:+()\-–—*×]/g, ' ').replace(/(^|\s)(ملليمتر|سنتيمتر|متر|أمتار|سم|مم|ملم|م|مل|لتر|كجم|جم)(?=$|\s)/g, ' ');
  // a dimension word left without its number says nothing on its own («عرض طول»)
  s = s.replace(/(^|\s)(?:ال)?(طول|عرض|ارتفاع|سمك|قطر)(?=$|\s)/g, ' ').replace(/(^|\s)[x×](?=$|\s)/g, ' ');
  return s.replace(/\s+/g, ' ').trim() || null;
}

const ATTR_NAME = { color: ['اللون', 'Colour'], size: ['المقاس', 'Size'], rest: ['النوع', 'Type'] };

/**
 * One axis → its split, or a reason it cannot be split.
 * → { mode: 'plain' | 'split' | 'folded', reason?, attrs?: [{ key, name_ar, name_en, values: [{ key, ar, en }] }],
 *     values?: [{ attrs: [valueIdx | null …], dims }], dims?: common dimensions, gemini?: true when the rules failed on combined labels }
 */
export function splitAxis(axis) {
  const vals = Array.isArray(axis?.values) ? axis.values : [];
  const n = vals.length;
  const longest = Math.max(0, ...vals.map((v) => String(v.label_ar || v.label_en || '').length));
  if (n <= 1) return { mode: 'plain', reason: 'single' };
  const parsed = vals.map((v) => ({ ...parseLabel(v.label_en || v.label_zh || v.label_ar), ar: v.label_ar }));
  // which parts vary: the attributes worth a row of chips
  const distinct = (k) => new Set(parsed.map((p) => (p[k] ? p[k].key || p[k] : null)).filter(Boolean));
  const kinds = ['color', 'size', 'rest'].filter((k) => distinct(k).size >= 2);
  const plain = () => (n <= FOLD_OVER && longest <= 40 ? { mode: 'plain', reason: 'short_list' } : null);
  if (kinds.length < 2) return plain() || { mode: 'folded', reason: 'one_attribute', gemini: combined(vals) };
  // the regular values carry every kind; the rest are outliers («Design 1»)
  const regular = parsed.map((p, i) => !NUMBERED_RE.test(vals[i].label_en || vals[i].label_zh || '') && kinds.every((k) => p[k] != null));
  const outliers = regular.filter((r) => !r).length;
  if (outliers > Math.max(1, Math.floor(n * OUTLIER_SHARE))) return plain() || { mode: 'folded', reason: 'inconsistent', gemini: combined(vals) };
  // a «type» that differs on every value is not an attribute — it is the whole label again
  const regN = n - outliers;
  const counts = kinds.map((k) => new Set(parsed.filter((p, i) => regular[i]).map((p) => p[k].key || p[k])).size);
  if (kinds.includes('rest') && counts[kinds.indexOf('rest')] > regN / 2) return plain() || { mode: 'folded', reason: 'type_unique', gemini: combined(vals) };
  // the attributes must cross: colour × size × type mostly filled, not one value per colour
  if (counts.some((c) => c < 2) || regN / counts.reduce((a, b) => a * b, 1) < MIN_COVERAGE) return plain() || { mode: 'folded', reason: 'not_crossed', gemini: combined(vals) };
  // build the attributes, in a stable order: colour, size (numeric), type
  const attrs = kinds.map((k) => {
    const seen = new Map();
    parsed.forEach((p, i) => {
      if (!regular[i] || !p[k]) return;
      const key = p[k].key || p[k];
      if (!seen.has(key)) seen.set(key, { key, en: k === 'rest' ? p.rest : key, ar: k === 'rest' ? null : p[k].ar, num: p[k].num ?? null, arVotes: {} });
      if (k === 'rest') { const a = arabicRest(vals[i].label_ar); if (a) seen.get(key).arVotes[a] = (seen.get(key).arVotes[a] || 0) + 1; }
    });
    let list = [...seen.values()];
    if (k === 'size' && list.every((x) => x.num != null)) list.sort((a, b) => a.num - b.num);
    list = list.map((x) => ({ key: x.key, en: x.en, ar: x.ar || Object.entries(x.arVotes).sort((a, b) => b[1] - a[1])[0]?.[0] || x.en }));
    if (k === 'rest') {
      list.forEach((x) => { if (isUkPlug({ label_en: x.en }) || parsed.some((p, i) => regular[i] && p.rest === x.key && isUkPlug(vals[i]))) { x.ar = UK_PLUG_AR; x.uk = true; } });
      list = [...list.filter((x) => x.uk), ...list.filter((x) => !x.uk)];   // the British plug first in its row
      // never an English «النوع» on an Arabic page (store test 2026-11-03): a type with no Arabic words
      // of its own means the rules misread the label — keep the supplier's own (translated) labels
      if (list.some((x) => x.ar === x.en && /[a-z]/i.test(String(x.en || '')))) return null;
    }
    return { key: k, name_ar: ATTR_NAME[k][0], name_en: ATTR_NAME[k][1], values: list };
  });
  if (attrs.some((a) => a === null)) return plain() || { mode: 'folded', reason: 'no_arabic', gemini: combined(vals) };
  // outliers become extra «type» values (or a type row of their own) with the other rows not applicable
  let typeAttr = attrs.find((a) => a.key === 'rest');
  if (outliers && !typeAttr) { typeAttr = { key: 'rest', name_ar: ATTR_NAME.rest[0], name_en: ATTR_NAME.rest[1], values: [] }; attrs.push(typeAttr); }
  const values = parsed.map((p, i) => {
    if (!regular[i]) {
      const key = `__${i}`;
      typeAttr.values.push({ key, en: vals[i].label_en || '', ar: vals[i].label_ar || vals[i].label_en || '', image_ref: vals[i].image_ref || null, alone: true });
      return { attrs: attrs.map((a) => (a === typeAttr ? typeAttr.values.length - 1 : null)), dims: p.dims };
    }
    return { attrs: attrs.map((a) => (a.key === 'rest' && !kinds.includes('rest') ? null : a.values.findIndex((x) => x.key === (p[a.key].key || p[a.key])))), dims: p.dims };
  });
  // every full selection must point at ONE value
  const sig = new Set();
  for (const v of values) { const k = v.attrs.join('|'); if (sig.has(k)) return plain() || { mode: 'folded', reason: 'ambiguous', gemini: combined(vals) }; sig.add(k); }
  // colour chips carry the picture of a value that has that colour
  const colorAttr = attrs.find((a) => a.key === 'color');
  if (colorAttr) colorAttr.values.forEach((cv, j) => { const i = values.findIndex((v) => v.attrs[attrs.indexOf(colorAttr)] === j); cv.image_ref = vals[i]?.image_ref || null; });
  const allDims = values.map((v) => v.dims).filter(Boolean);
  const same = allDims.length === n - outliers && allDims.every((x) => x.l === allDims[0].l && x.w === allDims[0].w && x.h === allDims[0].h);
  return { mode: 'split', attrs, values, dims: same ? allDims[0] : null };
}

// Labels that look like several facts in one string (worth asking Gemini when the rules fail).
function combined(vals) {
  const avg = vals.reduce((t, v) => t + String(v.label_en || '').length, 0) / Math.max(1, vals.length);
  const multi = vals.filter((v) => /\d/.test(v.label_en || '') && /[a-z]{3,}.*[a-z]{3,}/i.test(v.label_en || '')).length;
  return vals.length > FOLD_OVER && avg >= 14 && multi >= vals.length / 2;
}

/** A whole product's options → per axis, its split. */
export function splitProduct(options) {
  const axes = Array.isArray(options?.axes) ? options.axes : [];
  return axes.map((ax) => splitAxis(ax));
}

/** The product's overall mode for the admin list: split if any axis split; folded / failed otherwise. */
export function productMode(splits) {
  if (splits.some((s) => s.mode === 'split')) return 'split';
  if (splits.some((s) => s.mode === 'folded')) return 'folded';
  return 'plain';
}

/** «الأبعاد: 80 × 50 × 185 سم». */
export function dimsText(d) {
  if (!d) return null;
  return `${[d.l, d.w, d.h].filter((x) => x != null).join(' × ')} ${UNIT_AR[d.unit] || d.unit}`;
}

export const isArabicColor = (s) => COLOR_AR.has(s);
